import { ReactElement } from 'react'
import { NavLink } from 'react-router-dom'
import { getUnprocessableErrors } from '@10xlabs/utils'
import { Formik, getIn } from 'formik'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { ApolloError } from '@apollo/client'
import { IBusinessCustomerFormValues } from 'features/BusinessCustomer/types'
import { schema } from './schema'
import {
  GTBusinessClientAutocomplete,
  GTBusinessClientVertical,
  GTBusinessCustomerTypeInput,
  GTBusinessSiteSuggestion
} from 'gql/types.generated'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { isEmpty } from 'lodash'
import { Select } from 'components/v2/Select'
import { Input } from 'components/Input'
import styled from 'styled-components'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import { InputPhone } from 'components/Formik/InputPhone/InputPhone'
import { DEFAULT_COUNTRY_CODE } from 'components/Formik/InputPhone/country-codes-list'
import SelectKreditsCategory from 'features/Kredits/components/SelectKreditsCategory'
import { InputCurrency } from 'components/InputCurrency'
import { Label } from 'components/v2/Label'
import { canWrite } from 'features/Users/utils'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { useLogin } from 'features/Login/hooks'
import { getNextLink } from 'variables/filters'
import { EUrls } from 'utils/urls'

const messages = defineMessages({
  customerTypePlaceholder: {
    id: 'businessCustomer.form.customerType.placeholder',
    defaultMessage: 'Select type'
  },
  clientPlaceholder: {
    id: 'businessCustomer.form.client.placeholder',
    defaultMessage: 'Select client'
  },
  sitePlaceholder: {
    id: 'businessCustomer.form.site.placeholder',
    defaultMessage: 'Select site'
  },
  companyPlaceholder: {
    id: 'businessCustomer.form.company.placeholder',
    defaultMessage: 'Select a company'
  },
  emailPlaceholder: {
    id: 'businessCustomer.form.email.placeholder',
    defaultMessage: 'Enter an email'
  },
  firstNamePlaceholder: {
    id: 'businessCustomer.form.firstName.placeholder',
    defaultMessage: 'Enter a first name'
  },
  lastNamePlaceholder: {
    id: 'businessCustomer.form.lastName.placeholder',
    defaultMessage: 'Enter a last name'
  },
  phonePlaceholder: {
    id: 'businessCustomer.form.phone.placeholder',
    defaultMessage: 'Enter phone'
  },
  streetPlaceholder: {
    id: 'businessCustomer.form.street.placeholder',
    defaultMessage: 'Ej. Calle 6'
  },
  zipCodePlaceholder: {
    id: 'businessCustomer.form.zipCode.placeholder',
    defaultMessage: 'Enter Zip Code'
  },
  cityPlaceholder: {
    id: 'businessCustomer.form.city.placeholder',
    defaultMessage: 'Enter City'
  },
  latitudePlaceholder: {
    id: 'businessCustomer.form.latitude.placeholder',
    defaultMessage: 'Enter Latitude'
  },
  longitudePlaceholder: {
    id: 'businessCustomer.form.longitude.placeholder',
    defaultMessage: 'Enter Longitude'
  }
})

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`

interface IBusinessCustomerProps {
  initialValues?: IBusinessCustomerFormValues
  onSubmit: (values: IBusinessCustomerFormValues) => void
  loading: boolean
  error?: ApolloError
  isEdit?: boolean
  setOpenDrawer?: (value: boolean) => void
  isShowingKreditSettings?: boolean
  disableEditSite?: boolean
  disableEditClient?: boolean
}

const defaultValues: IBusinessCustomerFormValues = {
  kreditsSettings: {
    amount: '',
    category: ''
  },
  userAccountId: '',
  firstName: '',
  lastName: '',
  phoneNumber: {
    number: '',
    countryCode: DEFAULT_COUNTRY_CODE
  },
  email: '',
  customerType: GTBusinessCustomerTypeInput.Associate,
  businessSite: {
    id: '',
    name: ''
  },
  businessClient: {
    id: '',
    name: '',
    verticals: []
  },
  placeAddress: {
    street: '',
    zipCode: '',
    cityId: '',
    cityName: '',
    location: {
      latitude: '',
      longitude: ''
    }
  }
}

export const NewBusinessCustomer = ({
  isShowingKreditSettings = false,
  disableEditSite = false,
  disableEditClient = false,
  initialValues,
  onSubmit,
  loading,
  error,
  isEdit
}: IBusinessCustomerProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const initialFormValues: IBusinessCustomerFormValues = {
    kreditsSettings: {
      amount: '',
      category: ''
    },
    userAccountId: initialValues?.userAccountId || defaultValues.userAccountId,
    firstName: initialValues?.firstName || defaultValues.firstName,
    lastName: initialValues?.lastName || defaultValues.lastName,
    phoneNumber: {
      number: initialValues?.phoneNumber?.number || defaultValues.phoneNumber.number,
      countryCode: initialValues?.phoneNumber?.countryCode || defaultValues.phoneNumber.countryCode
    },
    email: initialValues?.email || defaultValues.email,
    customerType: initialValues?.customerType || defaultValues.customerType,
    businessSite: initialValues?.businessSite || defaultValues.businessSite,
    businessClient: initialValues?.businessClient || defaultValues.businessClient,
    placeAddress: {
      street: initialValues?.placeAddress?.street || defaultValues.placeAddress.street,
      zipCode: initialValues?.placeAddress?.zipCode || defaultValues.placeAddress.zipCode,
      cityId: initialValues?.placeAddress?.cityId || defaultValues.placeAddress.cityId,
      cityName: initialValues?.placeAddress?.cityName || defaultValues.placeAddress.cityName,
      location: {
        latitude: initialValues?.placeAddress?.location?.latitude || defaultValues.placeAddress.location.latitude,
        longitude: initialValues?.placeAddress?.location?.longitude || defaultValues.placeAddress.location.longitude
      }
    }
  }

  const canModifyCorporateKredits = canWrite(FEATURES.CORPORATE_KREDITS, login.accessToken.policies)

  return (
    <Formik initialValues={initialFormValues} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {({
        handleSubmit,
        dirty,
        values,
        handleBlur,
        handleChange,
        setFieldValue,
        touched,
        errors,
        setValues
      }): ReactElement => {
        const handleCityChange = (value: TSFixMe): void => {
          setValues({
            ...values,
            placeAddress: {
              ...values.placeAddress,
              cityName: value.name,
              cityId: value.id
            }
          })
        }
        const hasPlaceSaved = !isEmpty(initialValues?.placeAddress?.cityId)

        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <HeaderContainer>
              <Heading.H3>
                {isEdit ? (
                  <FormattedMessage id="businessCustomer.edit.title" defaultMessage="Edit Customer" />
                ) : (
                  <FormattedMessage id="businessCustomer.new.title" defaultMessage="Add Customer" />
                )}
              </Heading.H3>
            </HeaderContainer>
            <FormLayout.Card small>
              <Heading.H4>
                <FormattedMessage id="businessCustomer.form.title" defaultMessage="Customer" />
              </Heading.H4>
              <Row>
                <Column width="50%">
                  <BusinessClientAutocomplete
                    id="businessClient"
                    value={values.businessClient.name}
                    onChange={(businessClient: GTBusinessClientAutocomplete): void => {
                      setValues({
                        ...values,
                        businessSite: defaultValues.businessSite,
                        businessClient: {
                          id: businessClient.id,
                          name: businessClient.name,
                          verticals: businessClient.verticals ?? []
                        },
                        kreditsSettings: defaultValues.kreditsSettings
                      })
                    }}
                    placeholder={intl.formatMessage(messages.clientPlaceholder)}
                    label={<FormattedMessage id="businessSites.form.client.label" defaultMessage="Business client" />}
                    touched={touched.businessClient?.id}
                    errors={errors.businessClient?.id}
                    disabled={disableEditClient}
                  />
                </Column>
                <Column>
                  <BusinessSiteAutocomplete
                    id="businessSite"
                    value={values.businessSite.name}
                    onChange={(businessSite: GTBusinessSiteSuggestion): void => {
                      setValues({
                        ...values,
                        businessSite: {
                          id: businessSite.id,
                          name: businessSite.name
                        },
                        kreditsSettings: defaultValues.kreditsSettings
                      })
                    }}
                    placeholder={intl.formatMessage(messages.sitePlaceholder)}
                    label={<FormattedMessage id="businessSites.form.site.label" defaultMessage="Business site" />}
                    touched={touched.businessSite?.id}
                    errors={errors.businessSite?.id}
                    disabled={isEmpty(values.businessClient.id) || disableEditSite}
                    businessClientId={values.businessClient.id}
                  />
                </Column>
              </Row>
              <Row>
                <Column width="50%">
                  <Select
                    id="customerType"
                    value={values.customerType}
                    onChange={(e): void => {
                      setFieldValue('customerType', e.target.value)
                    }}
                    touched={Boolean(touched.customerType)}
                    errors={errors.customerType}
                    label={<FormattedMessage id="dictionary.type" defaultMessage="Type" />}
                    disabled={isEdit}
                  >
                    <option value="">{intl.formatMessage(messages.customerTypePlaceholder)}</option>
                    <option value={GTBusinessCustomerTypeInput.Associate}>
                      <FormattedMessage id="businessCustomer.form.customerType.associate" defaultMessage="Associate" />
                    </option>
                  </Select>
                </Column>
                {isShowingKreditSettings && canModifyCorporateKredits ? (
                  <Column width="50%">
                    <Row>
                      <Column>
                        <Label>
                          <FormattedMessage id="kredits.category" defaultMessage="Credits Category" />
                        </Label>
                        <SelectKreditsCategory
                          businessClientId={values.businessClient.id}
                          businessSiteId={values.businessSite.id}
                          name="kreditsSettings.category"
                          isRequiredSiteOrClient={true}
                          disabled={!isEmpty(values.kreditsSettings?.amount) || isEmpty(values.businessClient.id)}
                          onChange={(e): void => {
                            handleChange(e)
                            setFieldValue('kreditsSettings.amount', undefined)
                          }}
                        />
                      </Column>
                      <Column>
                        <Label>
                          <FormattedMessage id="kredits.amount" defaultMessage="Amount" />
                        </Label>
                        <InputCurrency
                          disabled={!isEmpty(values.kreditsSettings?.category) || !isEdit}
                          id="kreditsSettings.amount"
                          name="kreditsSettings.amount"
                          placeholder="Enter amount"
                          onChange={(value): void => {
                            setFieldValue('kreditsSettings.amount', value)
                            setFieldValue('kreditsSettings.category', undefined)
                          }}
                        />
                      </Column>
                    </Row>
                  </Column>
                ) : null}
              </Row>
            </FormLayout.Card>
            <FormLayout.Card small>
              <Heading.H4>
                <FormattedMessage id="businessCustomer.personalInfo.form.title" defaultMessage="Personal Information" />
              </Heading.H4>
              <Row noMargin>
                <Column width="50%">
                  <Input
                    id="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                    touched={touched.firstName}
                    errors={errors.firstName}
                    label={<FormattedMessage id="businessCustomer.firstName.label" defaultMessage="First name" />}
                  />
                </Column>
                <Column>
                  <Input
                    id="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                    touched={touched.lastName}
                    errors={errors.lastName}
                    label={<FormattedMessage id="businessCustomer.lastName.label" defaultMessage="Last name" />}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <InputPhone
                    fieldName="phoneNumber"
                    placeholder={intl.formatMessage(messages.phonePlaceholder)}
                    label={<FormattedMessage id="businessCustomer.phone.label" defaultMessage="Phone" />}
                  />
                </Column>
                <Column>
                  <Input
                    id="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                    touched={touched.email}
                    errors={errors.email}
                    label={<FormattedMessage id="businessCustomer.email.label" defaultMessage="EMAIL" />}
                  />
                </Column>
              </Row>
            </FormLayout.Card>
            {values.businessClient.verticals.includes(GTBusinessClientVertical.Team) && (
              <FormLayout.Card small>
                <Heading.H4>
                  <FormattedMessage
                    id="businessCustomer.form.titlePlace"
                    defaultMessage="Place <i>Optional</i>"
                    values={{ i: (text) => <i>{text}</i> }}
                  />
                </Heading.H4>
                <Row>
                  <Column width="50%">
                    <Input
                      id="placeAddress.street"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.placeAddress?.street}
                      placeholder={intl.formatMessage(messages.streetPlaceholder)}
                      touched={getIn(touched, 'placeAddress.street')}
                      errors={getIn(errors, 'placeAddress.street')}
                      label={<FormattedMessage id="companies.new.form.address.line1.label" defaultMessage="Street" />}
                      disabled={isEdit && hasPlaceSaved}
                    />
                  </Column>
                  <Column width="50%">
                    <Row>
                      <Column width="50%">
                        <Input
                          id="placeAddress.zipCode"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.placeAddress?.zipCode}
                          placeholder={intl.formatMessage(messages.zipCodePlaceholder)}
                          touched={getIn(touched, 'placeAddress.zipCode')}
                          errors={getIn(errors, 'placeAddress.zipCode')}
                          label={
                            <FormattedMessage id="companies.new.form.address.zipCode.label" defaultMessage="ZIP code" />
                          }
                          disabled={isEdit && hasPlaceSaved}
                        />
                      </Column>
                      <Column width="50%">
                        <CityAutocomplete
                          id="placeAddress.cityName"
                          value={values.placeAddress.cityName}
                          onChange={handleCityChange}
                          onBlur={handleBlur}
                          placeholder={intl.formatMessage(messages.cityPlaceholder)}
                          touched={getIn(touched, 'placeAddress.cityName')}
                          errors={getIn(errors, 'placeAddress.cityName')}
                          label={<FormattedMessage id="places.new.form.city.label" defaultMessage="City" />}
                          disabled={isEdit && hasPlaceSaved}
                        />
                      </Column>
                    </Row>
                  </Column>
                </Row>
                {!(isEdit && hasPlaceSaved) && (
                  <Row>
                    <Column>
                      <Input
                        id="placeAddress.location.latitude"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.placeAddress?.location?.latitude}
                        placeholder={intl.formatMessage(messages.latitudePlaceholder)}
                        touched={getIn(touched, 'placeAddress.location.latitude')}
                        errors={getIn(errors, 'placeAddress.location.latitude')}
                        label={<FormattedMessage id="places.new.form.latitude.label" defaultMessage="Latitude" />}
                        disabled={isEdit && hasPlaceSaved}
                      />
                    </Column>
                    <Column>
                      <Input
                        id="placeAddress.location.longitude"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.placeAddress?.location?.longitude}
                        placeholder={intl.formatMessage(messages.longitudePlaceholder)}
                        touched={getIn(touched, 'placeAddress.location.longitude')}
                        errors={getIn(errors, 'placeAddress.location.longitude')}
                        label={<FormattedMessage id="places.new.form.longitude.label" defaultMessage="Longitude" />}
                        disabled={isEdit && hasPlaceSaved}
                      />
                    </Column>
                  </Row>
                )}
              </FormLayout.Card>
            )}
            {error && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(error)}>
                  <FormattedMessage
                    id="businessCustomer.form.error"
                    defaultMessage="There was an error while creating your Customer"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button as={NavLink} kind="cancel" to={getNextLink(EUrls.BUSINESS_CUSTOMERS)}>
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" disabled={!dirty} loading={loading}>
                {isEdit ? (
                  <FormattedMessage id="dictionary.save" defaultMessage="Update" />
                ) : (
                  <FormattedMessage id="dictionary.create" defaultMessage="Save" />
                )}
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
